// prettier-ignore
import { ChangeDetectorRef, Component, DestroyRef, ElementRef, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { SelectedWorkspaceService } from '@cloud-twin-v2/cloud-twin-shared/selected-workspace.service';
import { LayerThematicStylesDto } from '@cloud-twin-v2/generated/simulation.generated.client';
import { AppModules } from '@common-modules/shared/app-modules.enum';
import { DialogService } from '@common-modules/shared/dialogs/dialogs.service';
import { DynamicRenderizerComponentService } from '@common-modules/shared/services/dynamic-renderizer-component.service';
import { WtrColors } from '@common-modules/shared/styles/wtr-colors';
import { LayerItemV2 } from '@common-modules/wlm-map/generic-map/models/layer-item';
import { LayerVisibilityChangeEvent } from '@common-modules/wlm-map/generic-map/models/layer-visibility-change-event';
import { MapGenericStyle } from '@common-modules/wlm-map/generic-map/models/map-generic-style';
import { GenericMapStylesService } from '@common-modules/wlm-map/generic-map/services/generic-map-styles.service';
import { SpinnerService } from '@common-modules/wlm-spinner/spinner.service';
import { TranslateService } from '@ngx-translate/core';
import Color from 'color';
import { finalize, of, switchMap } from 'rxjs';
import { LayerEventsService } from '../layers-selector/services/layer-events.service';
import { ThematicsService } from '../thematics/services/thematics.service';
import { BaseMapSidePanelComponent } from './base-map-side-panel.component';
import { MapSidePanelItemHostDirective } from './map-side-panel-item-host.directive';
import { MapSidePanelItemSettings, MapSidePanelSettings } from './map-side-panel-settings';

export class LayerItemStyleChangeData {
  layer: LayerItemV2;
  selectedStyle: MapGenericStyle;
  isUpdate?: boolean;
}

const COMPONENT_SELECTOR = 'wlm-side-panel';

@Component({
  selector: COMPONENT_SELECTOR,
  templateUrl: './side-panel.component.html',
  styleUrls: ['./side-panel.component.scss'],
  standalone: false,
})
export class SidePanelComponent implements OnInit {
  availableStyles: MapGenericStyle[] = [];
  layerItemColor: string = '#000000';
  private _selectedStyleId: string;
  get selectedStyleId(): string {
    return this._selectedStyleId;
  }
  set selectedStyleId(styleId: string) {
    this._selectedStyleId = styleId;
    this.selectedStyle = this.layerItemSelected.availableStyles.find(
      (style) => style.id === styleId,
    );
    if (this.selectedStyle) {
      this.layerItemColor = this._genericMapStylesService.getColorFromStyle(this.selectedStyle);
    }
  }
  selectedStyle: MapGenericStyle;
  formControl: FormControl = new FormControl();
  showThematicPopup = false;
  isEditThematicMode = false;

  @Input() map;

  private _layers: LayerItemV2[];
  public get layers(): LayerItemV2[] {
    return this._layers;
  }
  @Input() public set layers(value: LayerItemV2[]) {
    this._layers = value;
    if (value?.length) {
      this._layers = value;
      //this.updateLayerIcons(this._layers);
      this.getLayerGroupsByType(this._layers);
    }
  }

  @Output() layerItemStyleChange = new EventEmitter<LayerItemStyleChangeData>();

  @Input() sidePanelSettings: MapSidePanelSettings<any>;

  private _itemHosts: MapSidePanelItemHostDirective[];
  @ViewChildren(MapSidePanelItemHostDirective) set setiItemHosts(
    value: QueryList<MapSidePanelItemHostDirective>,
  ) {
    this._itemHosts = value?.toArray();
    if (this._itemHosts) {
      this.bindItemsToHosts();
    }
  }

  @Output() layerVisibilityChange = new EventEmitter<LayerVisibilityChangeEvent>();
  @Output() tabSelectionChange = new EventEmitter<boolean>();
  @Output() layerItemsOrderChanged = new EventEmitter<LayerItemV2[]>();

  selectedTab: string;
  topologyLayers: LayerItemV2[];
  thematicLayers: LayerItemV2[];
  defaultIconSize = 20;
  isOwnerOrSuperUser: boolean = false;

  private _layerItemSelected: LayerItemV2;
  public get layerItemSelected(): LayerItemV2 {
    return this._layerItemSelected;
  }
  public set layerItemSelected(value: LayerItemV2) {
    this._layerItemSelected = value;
    this.updateLayerItemSelected();
  }

  initialModel: LayerThematicStylesDto;
  readonly primaryColor = WtrColors.PrimaryColor;
  readonly T_SCOPE = `${AppModules.Map}.${COMPONENT_SELECTOR}`;

  private setLoading: (loading: boolean) => void;

  constructor(
    private readonly _dynamicRenderizerService: DynamicRenderizerComponentService,
    private readonly _element: ElementRef,
    private readonly _cd: ChangeDetectorRef,
    private readonly _genericMapStylesService: GenericMapStylesService,
    private readonly _layersService: LayerEventsService,
    private readonly _destroyRef: DestroyRef,
    private readonly _thematicsService: ThematicsService,
    private readonly _spinnerService: SpinnerService,
    private readonly _dialogService: DialogService,
    private readonly _translate: TranslateService,
    private readonly _selectedWorkspaceService: SelectedWorkspaceService,
  ) {
    this.setLoading = this._spinnerService.buildSetLoadingFn();
  }

  ngOnInit(): void {
    this._layersService.newThematic$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((thematic) => {
        this.showThematicPopup = false;
      });

    this._layersService.editThematic$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((thematic) => {
        this.showThematicPopup = false;
      });

    this._selectedWorkspaceService.isCurrentWorkspaceOwnerOrSuperUser$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((isOwnerOrSuperUser) => {
        this.isOwnerOrSuperUser = isOwnerOrSuperUser;
      });
  }

  private bindItemsToHosts(): void {
    if (this.sidePanelSettings?.items.length && this._itemHosts.length) {
      this.sidePanelSettings?.items.forEach(
        (itemSettings: MapSidePanelItemSettings<any>, index) => {
          // There will be as many hosts as item settings.
          const currentHost = this._itemHosts[index];
          this.injectIntoHost(itemSettings, currentHost);
        },
      );
    }
  }

  private injectIntoHost(
    itemSettings: MapSidePanelItemSettings<any>,
    host: MapSidePanelItemHostDirective,
  ): void {
    const viewContainerRef = host.viewContainerRef;

    const extendedPropertySetter = (component: BaseMapSidePanelComponent) => {
      if (itemSettings.propertySetter) {
        itemSettings.propertySetter(component);
      }
      component.itemSettings = itemSettings;
      component.mapData = {};
    };

    this._dynamicRenderizerService.injectComponentIntoViewContainer(
      itemSettings.component,
      viewContainerRef,
      extendedPropertySetter,
      itemSettings.featureInjector,
    );
  }

  getNativeElement() {
    return this._element.nativeElement;
  }

  private getLayerGroupsByType(layerItems: LayerItemV2[]) {
    this.topologyLayers = layerItems.filter((li) => !li.layerDefinition?.isThematic);
    this.thematicLayers = layerItems.filter((li) => li.layerDefinition?.isThematic);
  }

  onTopologyLayersOrderChanged(layerItems: LayerItemV2[]): void {
    this.topologyLayers = layerItems;
    this.setLayerItemsOrderChanged();
  }

  onThematicLayersOrderChanged(layerItems: LayerItemV2[]): void {
    this.thematicLayers = layerItems;
    this.setLayerItemsOrderChanged();
  }

  private setLayerItemsOrderChanged(): void {
    const allLayerItems = [...this.thematicLayers, ...this.topologyLayers];
    this.layerItemsOrderChanged.emit(allLayerItems);
  }

  onColorPickerChange(color: string): void {
    const formattedColor = this.mapToOutputAcceptedFormat(color);
    this.formControl.setValue(formattedColor);
    this.formControl.markAsDirty();
    this.layerItemColor = formattedColor;
  }

  private updateLayerItemSelected(): void {
    if (this.layerItemSelected?.layerStyle?.id) {
      this.selectedStyleId = this.layerItemSelected.layerStyle.id;
      this.availableStyles = this.layerItemSelected.availableStyles ?? [];
    }
  }

  mapToInputAcceptedFormat = (color: string): string => {
    const colorObject = new Color(color);
    const convertedColor = colorObject.rgb().string();
    return convertedColor;
  };

  mapToOutputAcceptedFormat = (color: string): string => {
    let colorObject = new Color(color);
    const convertedColor = colorObject.hexa();
    return convertedColor;
  };

  redirectColorPickerClick() {
    const colorPickerDiv = document.getElementById('colorPickerDiv');
    if (colorPickerDiv) {
      colorPickerDiv.click();
    }
  }

  selectTab(tabId: string): void {
    if (this.selectedTab === tabId) {
      this.selectedTab = null;
      this.layerItemSelected = undefined;
      this.showThematicPopup = false;
      this._cd.detectChanges();
      this.tabSelectionChange.emit(false);
    } else {
      this.selectedTab = tabId;
      this.tabSelectionChange.emit(true);
    }
  }

  closePopup(): void {
    this.layerItemSelected = undefined;
    this.showThematicPopup = false;
  }

  applyChanges(): void {
    if (this.layerItemSelected && this.selectedStyle) {
      this.layerItemStyleChange.emit({
        layer: this.layerItemSelected,
        selectedStyle: this.selectedStyle,
        isUpdate: true,
      });
    }
  }

  onOpenNewThematicPopup(): void {
    this.layerItemSelected = undefined;
    this.initialModel = new LayerThematicStylesDto();
    this.isEditThematicMode = false;
    this.showThematicPopup = true;
  }

  closeNewThematicPopup(): void {
    this.showThematicPopup = false;
  }

  onEditLayerItem(layerItem: LayerItemV2) {
    this.initialModel = this.mapToLayerThematicStylesDto(layerItem.layerStyle);
    this.layerItemSelected = undefined;
    this.isEditThematicMode = true;
    this.showThematicPopup = true;
  }

  private mapToLayerThematicStylesDto(style: any): LayerThematicStylesDto {
    return new LayerThematicStylesDto({
      id: style.id,
      styleName: style.styleName,
      workspaceId: style.workspaceId,
      layerType: style.layerType,
      fieldName: style.fieldName,
      objectType: style.objectType,
      styleDefinition: style.styleDefinition as any,
    });
  }

  onDeleteThematicLayer(layerItem: LayerItemV2) {
    const confirmMessageKey = this.getConfirmDeleteMessage('thematic.singular');
    this._dialogService
      .showTranslatedDialogMessage({
        translateKey: confirmMessageKey,
        icon: 'warning',
      })
      .pipe(
        switchMap((result) => {
          if (result.result) {
            this.setLoading(true);
            return this._thematicsService.deleteThematic(layerItem.layerStyle.id);
          } else {
            return of(null);
          }
        }),
        finalize(() => {
          this.setLoading(false);
        }),
      )
      .subscribe({
        next: (result) => {
          if (result) {
            this._layersService.deleteNewThematic(layerItem);
            this._dialogService.showEntityActionSnackBar('delete', 'thematic.singular');
          }
        },
        error: (error) => {
          this._dialogService.showErrorMessage(error);
        },
      });
  }

  getConfirmDeleteMessage(entityName: string): string {
    const entityNameTranslated = this._translate.instant(`common.entities.${entityName}`);
    return this._translate.instant(this.getConfirmDeleteMessageKey(), {
      entityName: entityNameTranslated,
    });
  }

  private getConfirmDeleteMessageKey(): string {
    return 'core.messages.delete-confirm';
  }
}
